import { css } from '@emotion/react';
import { theme } from './theme';
import type { Theme } from './theme';

/**
 * Generate global styles with the active theme.
 * Injects CSS custom properties so all components automatically adapt.
 */
export const createGlobalStyles = (t: Theme) => css`
  :root {
    /* Primary */
    --color-primary: ${t.colors.primary.main};
    --color-primary-light: ${t.colors.primary.light};
    --color-primary-dark: ${t.colors.primary.dark};

    /* Text */
    --color-text: ${t.colors.neutral.white};
    --color-text-secondary: ${t.colors.neutral.gray200};
    --color-text-muted: ${t.colors.neutral.gray300};

    /* Background */
    --color-bg: ${t.colors.background.main};
    --color-bg-card: ${t.colors.background.card};
    --color-bg-elevated: ${t.colors.neutral.whiteOpacity8};
    --color-bg-hover: ${t.colors.neutral.whiteOpacity20};

    /* Chat */
    --color-chat-user: ${t.colors.chat.userBg};
    --color-chat-assistant-text: ${t.colors.chat.assistantText};
    --color-chat-input-bg: ${t.colors.chat.inputBg};
    --color-chat-input-border: ${t.colors.chat.inputBorder};

    /* Cards */
    --color-card-bg: ${t.colors.background.card};

    /* Neutral */
    --color-border: ${t.colors.neutral.whiteOpacity20};
    --color-border-subtle: ${t.colors.neutral.whiteOpacity8};
    --color-surface: ${t.colors.neutral.charcoal};
    --color-surface-dark: ${t.colors.neutral.dark};
  }

  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  html,
  body {
    height: 100dvh;
    height: 100%;
    width: 100%;
    overflow: hidden;
  }

  body {
    font-family: ${t.typography.fontFamily.primary};
    font-size: ${t.typography.fontSize.base};
    font-weight: ${t.typography.fontWeight.regular};
    line-height: ${t.typography.lineHeight.normal};
    color: var(--color-text);
    background-color: var(--color-bg);
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    overflow: hidden;
  }

  /* Theme-specific background images */
  body[data-theme="dark"] {
    background-image: url('/img/bg.jpg');
    background-size: cover;
    background-position: top center;
    background-repeat: no-repeat;
    background-attachment: fixed;
  }

  body[data-theme="light"] {
    background-image: url('/img/bg-light.png');
    background-size: cover;
    background-position: top center;
    background-repeat: no-repeat;
    background-attachment: fixed;
  }

  #root {
    height: 100dvh;
    height: 100%;
    width: 100%;
    display: flex;
    flex-direction: column;
  }

  /* Custom scrollbar */
  ::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }

  ::-webkit-scrollbar-track {
    background: var(--color-bg-elevated);
    border-radius: ${t.borderRadius.sm};
  }

  ::-webkit-scrollbar-thumb {
    background: var(--color-surface-dark);
    border-radius: ${t.borderRadius.sm};
  }

  ::-webkit-scrollbar-thumb:hover {
    background: var(--color-primary);
  }

  /* Remove default button styles */
  button {
    background: none;
    border: none;
    cursor: pointer;
    font-family: inherit;
    padding: 0;
    color: inherit;
  }

  /* Remove default input styles */
  input,
  textarea {
    font-family: inherit;
    border: none;
    outline: none;
    background: none;
    color: inherit;
  }

  /* Disable text selection for UI elements */
  button,
  .no-select {
    user-select: none;
    -webkit-user-select: none;
  }

  /* Allow text selection in chat messages */
  .selectable {
    user-select: text;
    -webkit-user-select: text;
  }

  /* Disable tap highlight on mobile */
  * {
    -webkit-tap-highlight-color: transparent;
  }

  /* Safe area insets for iOS */
  @supports (padding: max(0px)) {
    body {
      padding-left: max(0px, env(safe-area-inset-left));
      padding-right: max(0px, env(safe-area-inset-right));
      padding-bottom: max(0px, env(safe-area-inset-bottom));
    }
  }
`;

// Backward compatibility
export const globalStyles = createGlobalStyles(theme);
